import React, { useState } from "react";
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Select,
  MenuItem,
  Snackbar,
  Typography,
  Stack,
  Tooltip,
} from "@mui/material";
import { DataGrid, GridRowId, type GridRenderCellParams, GridInputRowSelectionModel,GridRowSelectionModel  } from "@mui/x-data-grid";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import { LularDayPicker } from "./LularDatePicker";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import * as XLSX from 'xlsx'
import { FileUploader } from "./FileUploader";
import { BtcDkRecord, findBtcDk } from "../../services/btc/btcApi";
import { addOrUpdateBtcDk } from "../../features/btc/btcSlice";
import { useAppDispatch } from "../../app/hooks";
import { useIsMobile } from "../hooks/useIsMobile";
import { selectGrp } from "../../features/auth/authSlice";

/* ======================
   Types
====================== */
type ImportDkStepProps = {
  onImport: (records: BtcDkRecord[]) => void;
};

type CheckState = "new" | "conflict" | "error";

type CheckResult = {
  id: string;
  state: CheckState;
  candidateIdx: number;
  candidates: BtcDkRecord[];
};
type CommentAuthor = {
  name: string,
};
type CommentNode = {
  id: string,
  reactionsCount: number,
  subCommentsCount: number,
  content: string;
  url:string,
  children: CommentNode[];
  author: CommentAuthor
};

type PostRecord = {
  post_id: string,
  content: string,
  commentCount: number,
  comments: CommentNode[],
  url:string,
  [key: string]: unknown
};

type CommentRow = {
  id: string;
  title: string;
  question: string;
  answer: string;
  url: string;
  reactionsCount: number;
  subCommentsCount: number;
  paragraphs: string[];
  [key: string]: unknown;
};

type SumRecord = {
  traloi: number,
  like: number,
  chua: number
};

/* ======================
   Component
====================== */
const steps = ["Upload", "Chọn", "Kiểm tra", "Hoàn thành"];
const FIELDS = [
  { key: "ngay", alias: "Ngày" },
  { key: "email", alias: "Tài khoản" },
  { key: "Y1", alias: "Cúng dường" },
  { key: "Z1", alias: "Phí hàng tháng" },
  { key: "Z2", alias: "Phí hoạt động" },
  { key: "note", alias: "Ghi chú" },
]
export function ImportDkStep({ onImport }: ImportDkStepProps) {
  const dispatch = useAppDispatch();

  const [activeStep, setActiveStep] = useState<number>(0);
  const [previewData, setPreviewData] = useState<CommentRow[]>([]);
  const [sum, setSum] = useState<SumRecord>();
  const [rawData, setRawData] = useState<any[]>([]);
  const [excelColumns, setExcelColumns] = useState<any[]>([]);
  const [posts, setPosts] = useState<PostRecord[]>([]);
  const [success, setSuccess] = useState<boolean>(false);
  const [selectedIds, setSelectedIds] = useState<GridRowSelectionModel>([]);
  const [columnMap, setColumnMap] = useState<any>({});
  const [selectRows, setSelectRows] = useState<any[]>([]);
  const [selectColumns, setSelectColumns] = useState<any[]>([]);
  const [checkResults, setCheckResults] = useState<CheckResult[]>([]);
  const [createdAtMs, setCreatedAtMs] = useState<number>();
  const [path, setPath] = useState("");
  const isMobile = useIsMobile();
  const isValidDay = createdAtMs && (dayjs(createdAtMs).year() > 2021);
  const grp = useSelector(selectGrp);
  /* ======================
     STEP 1: Upload JSON
  ====================== */
  const handleUpload = (file: File|null) => {
    // const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onload = (evt: ProgressEvent<FileReader>) => {
      const result = evt.target?.result;

      // console.log("file content", result);
      const arr = String(result).split("\n");
      // remove empty row, empty column
      const rawData = clean2DArray(arr.map(row=>row.split("\t")));
      
      const maxColumn = Math.max(...rawData.map((row) => row.length), 0);

      const excelColumns = Array.from(
        { length: maxColumn },
        (_, i) => `col_${i}`,
      );

      const previewData = rawData.slice(0, 10).map((row, rowIndex) => {
        const obj = { id: rowIndex } as any;

        excelColumns.forEach((col, colIndex) => {
          obj[col] = row[colIndex] ?? "";
        });

        return obj;
      });

      setExcelColumns(excelColumns);
      setPreviewData(previewData);
      setRawData(rawData);
      nextStep();
    };

    reader.readAsText(file);
  };

  // STEP 2: chọn Dks
  const selectDks = () => {
    const mappedColumns = Object.entries(columnMap)
      .filter(([_, field]) => field) // chỉ giữ cột có field
      .map(([excelCol, field]) => ({
        excelCol,
        field,
      }));

    const selectRows = rawData.map((row, rowIndex) => {
      const obj = { id: rowIndex } as any;

      mappedColumns.forEach(({ excelCol, field }) => {
        const colIndex = Number(excelCol.replace("col_", ""));
        obj[String(field)] = row[colIndex] ?? "";
      });

      return obj;
    });

    const selectColumns = mappedColumns.map(({ field }) => ({
      field,
      headerName: String(field).toUpperCase(),
      minWidth: 160,
      flex: 1,
    }));
    setSelectRows(selectRows);
    setSelectColumns(selectColumns);
    nextStep();
  };

  /* ======================
     STEP 3: Check existence
  ====================== */
  const handleCheck = async () => {
    console.log("handleCheck");

    // convert selected comment to Title {titleId, title, paragraphs, path, createdAtMs}

    const results = await Promise.all(
      selectedIds.map(async (i) => {
        const row = selectRows[Number(i)];
        const r = checkResults.find((r) => r.id === row.id);
        if (r) {
          return r;
        }

        // if is checked skip
        const existed = checkResults.find((r) => r.id === row.id);
        if (existed) return existed;

        const { result, error } = await findBtcDk(row.ngay,row.email, grp);
        console.log("findRecord", row.id, result)
        const records: BtcDkRecord[] = result ?? [];

        const newResult = {
          id: row.id,
          candidateIdx: 0,
          state: "error",
          candidates: [{}],
        } as CheckResult;

        if (error) {
          // newResult.candidates = [row];
        } else {
          // transform  to BtcDkRecord
          const newRecord = {
            ngay: row.ngay,
            email: row.email,
            note: row.note || "",
            dsQuy: [] as any [],
            id: `tmp_${crypto.randomUUID()}`,
          } as BtcDkRecord;

          ["Y1", "Z1", "Z2"].forEach((msQuy) => {
            if (row[msQuy]) {
              newRecord.dsQuy.push({
                msQuy,
                soLuong: Number(row[msQuy]),
                donVi: "y",
              });
            }
          });

          if (records.length > 0) {
            newResult.state = "conflict";
            newResult.candidates = [newRecord, ...records];

            // update store
            dispatch(addOrUpdateBtcDk(records));
          } else {
            newResult.state = "new";
            newResult.candidates = [newRecord];
          }
        }
        return newResult;
      }),
    );

    setCheckResults(results);
    nextStep();
  };

  const nextStep = () => setActiveStep(activeStep + 1);
  const prevStep = () => setActiveStep(activeStep - 1);

  const columns = excelColumns.map((col) => ({
    field: col,
    minWidth: 160,
    flex: 1,
    sortable: false,

    renderHeader: () => (
      <Select
        size="small"
        value={String(columnMap[String(col)]) || ""}
        onChange={(e) =>
          setColumnMap((prev : any) => ({
            ...prev,
            [col]: e.target.value,
          }))
        }
      >
        <MenuItem value="">-- Không import --</MenuItem>
        {FIELDS.map((f) => (
          <MenuItem key={f.key} value={f.key}>
            {f.alias}
          </MenuItem>
        ))}
      </Select>
    ),
  }));

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {activeStep === 0 && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            flexGrow: 10,
          }}
        >
          {/* preview */}
          <Box sx={{ flexGrow: 1 }}>
            {/* drag/drop */}
            <FileUploader
              accept={".json"}
              onChange={handleUpload}
            ></FileUploader>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            {rawData ? (
              <Button variant="outlined" onClick={() => setRawData([])}>
                Clear
              </Button>
            ) : (
              <Box></Box>
            )}

            <Stack direction={"row"} spacing={1}>
              <Button
                disabled={!rawData || !isValidDay}
                variant="contained"
                onClick={nextStep}
              >
                Tiếp tục
              </Button>
            </Stack>
          </Box>
        </Box>
      )}

      {activeStep === 1 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Gán tên cột
          </Typography>

          <DataGrid
            rows={previewData}
            columns={columns}
            disableRowSelectionOnClick
            autoHeight // ✅ grid tự co chiều cao
            hideFooter // ✅ Ẩn toàn bộ footer (pagination)
            disableColumnMenu // ✅ Không cần menu
            density="compact"
            columnHeaderHeight={88}
          />
          <Box display="flex" justifyContent="space-between" mt={2}>
            <Button variant="outlined" onClick={prevStep}>
              ◀ Back (Upload)
            </Button>
            <Button
              variant="contained"
              sx={{ mt: 2 }}
              onClick={() => selectDks()}
            >
              Tiếp tục
            </Button>
          </Box>
        </Box>
      )}

      {activeStep === 2 && (
        <Box>
          <Typography>Chọn DK muốn import</Typography>

          <Box sx={{ height: 400, width: "100%" }}>
            <DataGrid
              rows={selectRows}
              columns={selectColumns}
              checkboxSelection
              disableRowSelectionOnClick
              density="compact"
              rowSelectionModel={selectedIds}
              onRowSelectionModelChange={(ids) => {
                setSelectedIds(ids);
              }}
              autoPageSize
            />
          </Box>

          <Box display="flex" justifyContent="space-between" mt={2}>
            <Button variant="outlined" onClick={prevStep}>
              ◀ Back (Chọn column)
            </Button>

            <Button
              variant="contained"
              disabled={selectedIds.length === 0}
              onClick={handleCheck}
            >
              Check ({selectedIds.length})
            </Button>
          </Box>
        </Box>
      )}

      {activeStep === 3 && (
        <>
          <CheckTable
            checkResults={checkResults}
            setCheckResults={setCheckResults}
          />
          <Box sx={{ mt: 2, display: "flex", justifyContent: "space-between" }}>
            <Button onClick={prevStep}>BACK</Button>
            <Button
              variant="contained"
              onClick={() => {
                const records = selectedIds.map((id) => {
                  const r = checkResults.find((x) => x.id === id)!;
                  return r.candidates[r.candidateIdx];
                });

                onImport(records);
              }}
            >
              Import ({selectedIds.length})
            </Button>
          </Box>
        </>
      )}

      <Snackbar
        open={success}
        autoHideDuration={3000}
        message="Import user thành công"
        onClose={() => setSuccess(false)}
      />
    </Box>
  );
}

/* ======================
   CheckTable
====================== */
type CheckTableProps = {
  checkResults: CheckResult[];
  setCheckResults: React.Dispatch<React.SetStateAction<CheckResult[]>>;
};

function CheckTable({ checkResults, setCheckResults }: CheckTableProps) {
  const handleCandidateChange = (id: string, candidateIdx: number) => {
    setCheckResults((prev) =>
      prev.map((r) => (r.id === id ? { ...r, candidateIdx } : r)),
    );
  };

  const rows = checkResults.map((r) => {
    const record = r.candidates[r.candidateIdx];
    return {
      ...record,
      state: r.state,
      candidateIdx: r.candidateIdx,
      options: r.candidates.map((_, idx: number) => ({
        value: idx,
        label: `[${idx}] ${idx===0?"Bản mới": "Bản cũ"}`,
      })),
      resultId: r.id,
    };
  });

  const columns = [
    {
      field: "state",
      headerName: "Trạng thái",
      width: 40,
      renderCell: (params: GridRenderCellParams) =>
        params.value === "new" ? (
          <CheckCircleIcon color="success" fontSize="small" />
        ) : params.value === "conflict" ? (
          <WarningAmberIcon color="warning" fontSize="small" />
        ) : null,
    },
    {
      field: "ngay",
      headerName: "Ngày",
      flex: 1,
      renderCell: (params: any) => {
        return params.row.ngay;
      },
    },
    {
      field: "email",
      headerName: "Tài khoản",
      flex: 1,
      renderCell: (params: any) => {
        return params.row.email;
      },
    },
    {
      field: "dsQuy",
      headerName: "Chi tiết",
      flex: 1,
      renderCell: (params: any) => {
        return params.row.dsQuy.map((item:any)=>`${item.msQuy}:${item.soLuong}(${item.donVi})`).join("\n");
      },
    },
    {
      field: "note",
      headerName: "Ghi chú",
      flex: 1,
    },
    {
      field: "Phiên bản",
      width: 180,
      renderCell: (params: GridRenderCellParams) => (
        <Select
          size="small"
          fullWidth
          value={params.row.candidateIdx}
          onChange={(e) =>
            handleCandidateChange(params.row.resultId, Number(e.target.value))
          }
        >
          {params.row.options.map((opt: any) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      ),
    },
  ];

  return <DataGrid rows={rows} columns={columns} autoHeight disableRowSelectionOnClick />;
}

function InfoRow({ label, value }: { label: string; value: string|number }) {
  if (!value) return null;

  return (
    <Box sx={{ display: "flex", gap: 1 }}>
      <Typography
        variant="body2"
        sx={{ minWidth: 140, color: "text.secondary" }}
      >
        {label}:
      </Typography>
      {label === "url" ? (
        <Tooltip title={value}>
          <Typography
            variant="body2"
            component="a"
            href={value as string}
            target="_blank"
            rel="noopener noreferrer"
            noWrap
            sx={{
              maxWidth: "100%",
              display: "block",
              color: "primary.main",
              textDecoration: "none",
            }}
          >
            {value}
          </Typography>
        </Tooltip>
      ) : (
        <Typography
          variant="body2"
          noWrap
          sx={{ maxWidth: 200 }} // required for ellipsis to work
        >
          {value}
        </Typography>
      )}
    </Box>
  );
}

function splitFirstLine(text: string) {
  const idx = text.indexOf("\n");

  if (idx === -1) {
    return {
      title: text,
      question: "",
    };
  }

  return {
    title: text.slice(0, idx),
    question: text.slice(idx + 1),
  };
}
function trace(comment:CommentNode) {
  const items: any[] = [];
  if (comment.content) {
      items.push({author:comment.author.name, content: comment.content});
    }

    if (Array.isArray(comment.children)) {
      for (const child of comment.children) {
        const childItems = trace(child);
        items.push(childItems);
      }
    }
    return items.flat();
}
function parseComment(comment: CommentNode): {
  title: string;
  question: string;
  answer: string;
  paragraphs: string[];
} {
  const CHI_DAY = "BLĐ chỉ dạy";
  const { title, question } = splitFirstLine(comment.content);
  const paragraphs = ["Câu hỏi", question];
  const answerLst = [];
  const questionLst = [question];
  const items = trace(comment);
  for (let i = 1; i < items.length; i++) {
    if (items[i].author.match(/Ban Lãnh Đạo/gi)) {
      answerLst.push(items[i].content);

      paragraphs.push(CHI_DAY);
      paragraphs.push(items[i].content);
    } else {
      questionLst.push(items[i].content);

      paragraphs.push("Câu hỏi");
      paragraphs.push(items[i].content);
    }
  }

  return {
    title,
    question: questionLst.join("\r\n\r\n"),
    answer: answerLst.join("\r\n\r\n"),
    paragraphs,
  };
}

const renderStateIcon = ({row}:GridRenderCellParams ) => {
  const state = hasText(row.answer)
    ? "traloi"
    : row.reactionsCount > 0
      ? "like"
      : "chua";

  const STATE_ICON_MAP: Record<string, React.ReactNode> = {
    traloi: <CheckCircleIcon color="success" fontSize="small" />,
    like: <ThumbUpIcon color="primary" fontSize="small" />,
    chua: <WarningAmberIcon color="warning" fontSize="small" />,
  };

  return STATE_ICON_MAP[state] ?? null;
};
const renderLink = ({row}:GridRenderCellParams ) => {
  return (
    <a href={row.url} target="_blank" rel="noopener noreferrer">
      {row.id}
    </a>
  );
};


type CommentTableProps = {
  rows: CommentRow[];
  selectedIds: string[];
  setSelectedIds: React.Dispatch<
    React.SetStateAction<string[]>
  >;
};

function CommentTable({ rows, selectedIds, setSelectedIds }:CommentTableProps) {
  // const [rowSelectionModel, setRowSelectionModel] =
  // React.useState<GridRowSelectionModel>({
  //   type: 'include', // or 'exclude'
  //   ids: new Set<GridRowId>(selectedIds),
  // });

  // useEffect(() => {
  //   const newModel : GridRowSelectionModel = {type: 'include', ids: new Set(selectedIds)};
  //   setRowSelectionModel(newModel);
  // }, [selectedIds]);


  const columns = [
    {
      field: "state",
      headerName: "Trạng thái",
      width: 40,
      sortable: false,
      renderCell: (params:GridRenderCellParams ) => renderStateIcon(params),
    },
    { field: "title", headerName: "Tiêu đề", flex: 1 },
    { field: "question", headerName: "Câu hỏi", flex: 1 },
    { field: "answer", headerName: "Chỉ dạy", flex: 1 },
    {
      field: "url",
      headerName: "Link",
      flex: 1,
      renderCell: (params:GridRenderCellParams ) => renderLink(params),
    },
  ];
  return (
    <DataGrid
      rows={rows}
      columns={columns}
      checkboxSelection
      disableRowSelectionOnClick
      density="compact"
    />
  );
}

function hasText(v: unknown): v is string {
  return typeof v === "string" && v.trim().length > 0;
}

function clean2DArray(data: string[][]): string[][] {
  if (!data || data.length === 0) return [];

  // 1. Remove empty rows
  const nonEmptyRows = data.filter(row =>
    row.some(cell => cell != null && cell.trim() !== "")
  );

  if (nonEmptyRows.length === 0) return [];

  // 2. Find columns to keep
  const colCount = Math.max(...nonEmptyRows.map(r => r.length));

  const colsToKeep: number[] = [];

  for (let col = 0; col < colCount; col++) {
    const hasValue = nonEmptyRows.some(row => {
      const cell = row[col];
      return cell != null && cell.trim() !== "";
    });

    if (hasValue) colsToKeep.push(col);
  }

  // 3. Remove empty columns
  return nonEmptyRows.map(row =>
    colsToKeep.map(col => row[col] ?? "")
  );
}